import { useState, useEffect } from "react";
import { LayoutDashboard, ChartBar, Settings, User, ShieldCheck, Users, Image } from "lucide-react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

export function DashboardSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCreator, setIsCreator] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        navigate('/');
      } else if (session) {
        checkUserStatus();
      }
    });

    checkUserStatus();

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  const checkUserStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error("Please sign in to access the dashboard");
        navigate('/');
        return;
      }

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('creator_status, is_superuser')
        .eq('id', session.user.id)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (profile) {
        setIsCreator(profile.creator_status === 'creator');
        setIsSuperuser(profile.is_superuser);
      }
    } catch (error) {
      console.error("Error checking user status:", error);
      toast.error("Failed to load user profile");
    }
  };

  const menuItems = [
    {
      title: "Overview",
      url: "/dashboard",
      icon: LayoutDashboard,
      showFor: "all",
    },
    {
      title: "Analytics",
      url: "/dashboard/analytics",
      icon: ChartBar,
      showFor: "creator",
    },
    {
      title: "Profile",
      url: "/profile",
      icon: User,
      showFor: "all",
    },
    {
      title: "Admin Dashboard",
      url: "/dashboard/admin",
      icon: ShieldCheck,
      showFor: "superuser",
    },
    {
      title: "User Management",
      url: "/admin/users",
      icon: Users,
      showFor: "superuser",
    },
    {
      title: "Media Kit",
      url: "/admin/media-kit",
      icon: Image,
      showFor: "superuser",
    },
    {
      title: "Settings",
      url: "/dashboard/settings",
      icon: Settings,
      showFor: "all",
    },
  ];

  const filteredMenuItems = menuItems.filter(item => 
    item.showFor === "all" || 
    (item.showFor === "creator" && isCreator) ||
    (item.showFor === "superuser" && isSuperuser)
  );

  return (
    <Sidebar>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredMenuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <Link
                      to={item.url}
                      className={`flex items-center gap-3 ${
                        location.pathname === item.url
                          ? "text-primary"
                          : "text-muted-foreground"
                      }`}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
